var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
import { SkinnedMesh, MeshBasicMaterial } from "three";
import { generateTextureFromImage, generateFinish, generateSubstrate, isPow2, loadImage, releaseCanvases } from "../common";
export class MaterialManager {
  constructor(config) {
    Object.defineProperty(this, "environment", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    // Store for created materials and highlighted areas
    Object.defineProperty(this, "materialMap", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: {}
    });
    Object.defineProperty(this, "hightLightMap", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: {}
    });
    // Canvases to store the current state of the documents keyed by page and finish.
    // These canvases can be re-used while the same product is loaded because
    // the amount and sizes of the canvases won't change.
    Object.defineProperty(this, "documentCanvases", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: {}
    });
    // Assets to store temporary textures and the canvases they use. These assets
    // should be flushed whenever the document or product changes.
    Object.defineProperty(this, "tempCanvases", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: {}
    });
    Object.defineProperty(this, "materialTextures", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: {}
    });
    // Assets to store the states of materials and their imagery. These canvases can
    // be re-used while the same product is loaded because the product images and substrates
    // won't change.
    Object.defineProperty(this, "materialImages", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: {}
    });
    Object.defineProperty(this, "materialCanvases", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: {}
    });
    Object.defineProperty(this, "renderingOptions", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    this.renderingOptions = config.renderingOptions;
    if (this.renderingOptions.minimumRenderSize && !isPow2(this.renderingOptions.minimumRenderSize)) {
      throw new Error("Minium render size must be a power of two!");
    }
    if (this.renderingOptions.maximumRenderSize && !isPow2(this.renderingOptions.maximumRenderSize)) {
      throw new Error("Maximum render size must be a power of two!");
    }
    this.environment = config.environment;
  }
  getDocumentSurfaces(product) {
    const documentSurfaces = {};
    const surfaceMap = product.getSurfaceMapping();
    Object.values(surfaceMap).forEach(s => {
      if (s.page) {
        // Add the base page
        const key = "".concat(s.page, " none");
        if (!documentSurfaces[key]) {
          documentSurfaces[key] = {
            page: s.page,
            fullBleed: s.fullBleed,
            trim: s.trim,
            textureSize: this.getTextureSize(s),
            preserveAlpha: s.substrate.supportsTransparency || s.substrate.requiresDocumentTransparency
          };
        } else if (s.substrate.supportsTransparency || s.substrate.requiresDocumentTransparency) {
          // document with same page reference might have different substrate references
          documentSurfaces[key].preserveAlpha = true;
        }
        // Add any premium finishes
        if (s.finishes) {
          for (const f of s.finishes) {
            const finishKey = "".concat(s.page, " ").concat(f.channel);
            if (!documentSurfaces[finishKey]) {
              documentSurfaces[finishKey] = {
                page: s.page,
                channel: f.channel,
                fullBleed: s.fullBleed,
                trim: s.trim,
                textureSize: this.getTextureSize(s),
                preserveAlpha: true
              };
            }
          }
        }
      }
    });
    return Object.values(documentSurfaces);
  }
  setDocumentCanvas(surface, canvas) {
    var _a;
    const key = "".concat(surface.page, " ").concat((_a = surface.channel) !== null && _a !== void 0 ? _a : "none");
    // Add a canvas if one doesn't exist from previous updates
    if (!this.documentCanvases[key]) {
      this.documentCanvases[key] = document.createElement("canvas");
    }
    const canvasContext = this.documentCanvases[key].getContext("2d");
    // Get the raster size
    const {
      width,
      height
    } = surface.textureSize;
    // Update the canvas to the current texture size
    this.documentCanvases[key].width = width;
    this.documentCanvases[key].height = height;
    // Clear the canvas from any previous operations
    canvasContext.clearRect(0, 0, width, height);
    // TODO required?
    // Fill the canvas with a white background when alpha doesn't need to be preserved.
    // This is required because transparency will turn black when draw
    if (!surface.preserveAlpha) {
      canvasContext.fillStyle = "white";
      canvasContext.fillRect(0, 0, width, height);
    }
    // If the surface has a trim, crop the input canvas accordingly
    if (surface.trim) {
      // Build the sub rectangle from the physical trim coordinates and convert into pixels
      const sx = surface.trim.topLeftX / surface.fullBleed.width * canvas.width;
      const sy = surface.trim.topLeftY / surface.fullBleed.height * canvas.height;
      const sWidth = (surface.trim.bottomRightX - surface.trim.topLeftX) / surface.fullBleed.width * canvas.width;
      const sHeight = (surface.trim.bottomRightY - surface.trim.topLeftY) / surface.fullBleed.height * canvas.height;
      canvasContext.drawImage(canvas, Math.floor(sx), Math.floor(sy), Math.floor(sWidth), Math.floor(sHeight), 0, 0, width, height);
    } else {
      canvasContext.drawImage(canvas, 0, 0, width, height);
    }
  }
  updateColor(surfaceId, color) {
    // Resolve a race condition where highlights happen during geometry updates
    if (!this.materialMap[surfaceId]) return;
    const material = this.materialMap[surfaceId];
    material.color = color;
    material.needsUpdate = true;
  }
  restoreColor(surfaceId) {
    // Resolve a race condition where highlights happen during geometry updates
    if (!this.materialMap[surfaceId]) return;
    const material = this.materialMap[surfaceId];
    // Restore the material color
    material.color = this.hightLightMap[surfaceId].clone();
    material.needsUpdate = true;
  }
  setWireframes(enabled) {
    if (!this.materialMap) return;
    Object.values(this.materialMap).forEach(material => {
      material.wireframe = enabled;
      material.needsUpdate = true;
    });
  }
  fetchMaterialTextures(product) {
    return __awaiter(this, void 0, void 0, function* () {
      const surfaceMap = product.getSurfaceMapping();
      const imagePromises = {};
      // Fetch the environment reflections
      if (this.environment.reflectionUrl && !imagePromises[this.environment.reflectionUrl]) {
        imagePromises[this.environment.reflectionUrl] = loadImage(this.environment.reflectionUrl);
      }
      // Fetch the environment backgrounds
      if (this.environment.skyBox) {
        imagePromises[this.environment.skyBox.textureUrl] = loadImage(this.environment.skyBox.textureUrl);
      }
      // Fetch the environment reflections (alternative method)
      if (this.environment.reflectionUrls) {
        for (const url of this.environment.reflectionUrls) {
          if (!imagePromises[url]) {
            imagePromises[url] = loadImage(url);
          }
        }
      }
      // Build the substrate and/or finish for every product surface
      Object.keys(surfaceMap).forEach(surfaceKey => {
        const {
          substrate,
          finishes
        } = surfaceMap[surfaceKey];
        // Fetch the base texture
        if (substrate.baseTexture && !imagePromises[substrate.baseTexture.sourceUrl]) {
          imagePromises[substrate.baseTexture.sourceUrl] = loadImage(substrate.baseTexture.sourceUrl);
        }
        // Fetch the bump map if it doesn't exist
        if (substrate.bumpTexture && !imagePromises[substrate.bumpTexture.sourceUrl]) {
          imagePromises[substrate.bumpTexture.sourceUrl] = loadImage(substrate.bumpTexture.sourceUrl);
        }
        // Fetch the specular map if it doesn't exist
        if (substrate.specularTexture && !imagePromises[substrate.specularTexture.sourceUrl]) {
          imagePromises[substrate.specularTexture.sourceUrl] = loadImage(substrate.specularTexture.sourceUrl);
        }
        // Fetch the alpha map if it doesn't exist
        if (substrate.alphaTexture && !imagePromises[substrate.alphaTexture.sourceUrl]) {
          imagePromises[substrate.alphaTexture.sourceUrl] = loadImage(substrate.alphaTexture.sourceUrl);
        }
        // Fetch the overlay texture if it exists
        if (substrate.overlayTexture && !imagePromises[substrate.overlayTexture.sourceUrl]) {
          imagePromises[substrate.overlayTexture.sourceUrl] = loadImage(substrate.overlayTexture.sourceUrl);
        }
        // Fetch the refection map if it doesn't exist
        if (substrate.reflectionMapOverrideUrls) {
          for (const url of substrate.reflectionMapOverrideUrls) {
            if (!imagePromises[url]) {
              imagePromises[url] = loadImage(url);
            }
          }
        }
        // Load finishes if they exist
        if (finishes) {
          finishes.forEach(finish => {
            // Fetch the base texture
            if (finish.baseTexture && !imagePromises[finish.baseTexture.sourceUrl]) {
              imagePromises[finish.baseTexture.sourceUrl] = loadImage(finish.baseTexture.sourceUrl);
            }
            // Fetch the specular map if it doesn't exist
            if (finish.specularTexture && !imagePromises[finish.specularTexture.sourceUrl]) {
              imagePromises[finish.specularTexture.sourceUrl] = loadImage(finish.specularTexture.sourceUrl);
            }
            // Fetch the alpha map if it doesn't exist
            if (finish.alphaTexture && !imagePromises[finish.alphaTexture.sourceUrl]) {
              imagePromises[finish.alphaTexture.sourceUrl] = loadImage(finish.alphaTexture.sourceUrl);
            }
            // Fetch the refection map if it doesn't exist
            if (finish.reflectionMapOverrideUrls) {
              for (const url of finish.reflectionMapOverrideUrls) {
                if (!imagePromises[url]) {
                  imagePromises[url] = loadImage(url);
                }
              }
            }
          });
        }
      });
      const loadingPromises = [];
      // Setup each promise to execute in parallel and populate the material map
      Object.keys(imagePromises).forEach(key => {
        loadingPromises.push(new Promise(resolve => {
          imagePromises[key].then(image => {
            this.materialImages[key] = image;
            resolve();
          });
        }));
      });
      yield Promise.all(loadingPromises);
    });
  }
  updateTextures(product, meshMap, skyBox) {
    this.flushDocumentAssets();
    const surfaceMap = product.getSurfaceMapping();
    this.buildMaterialMap(surfaceMap, meshMap);
    // Texture each mesh defined in the vortex product
    Object.keys(meshMap).forEach(meshId => {
      if (this.materialMap[meshId]) {
        const material = this.materialMap[meshId];
        // morphTarget meshes need morphTarget flag in material to be true to render animations correctly
        // morphTargetsRelative is a boolean but might also be undefined
        if (meshMap[meshId].geometry.morphTargetsRelative) {
          material.morphTargets = true;
        }
        // eslint-disable-next-line no-param-reassign
        meshMap[meshId].material = material;
      } else {
        throw new Error("No product surface was defined for mesh: ".concat(meshId));
      }
    });
    if (this.environment.skyBox && skyBox) {
      const skyBoxTexture = this.materialTextures[this.environment.skyBox.textureUrl];
      skyBox.material = new MeshBasicMaterial({
        map: skyBoxTexture,
        depthWrite: false
      });
    }
  }
  getTextureSize(surface) {
    var _a, _b;
    const aspectRatio = surface.fullBleed.width / surface.fullBleed.height;
    // Define the texture size constraints. Flat sheet products can be rendered smaller
    const minTextureSize = (_a = this.renderingOptions.minimumRenderSize) !== null && _a !== void 0 ? _a : 1024;
    const maxTextureSize = (_b = this.renderingOptions.maximumRenderSize) !== null && _b !== void 0 ? _b : 2048;
    let width;
    let height;
    if (aspectRatio > 1.5) {
      width = maxTextureSize;
      height = minTextureSize;
    } else if (aspectRatio > 0.5) {
      width = maxTextureSize;
      height = maxTextureSize;
    } else {
      height = maxTextureSize;
      width = minTextureSize;
    }
    return {
      width,
      height
    };
  }
  dispose() {
    // Flush all resources during document changes
    this.flushDocumentAssets();
    // Release all the canvases used when a product changes
    releaseCanvases([...Object.values(this.documentCanvases), ...Object.values(this.materialCanvases)]);
    // Release material maps for garbage collection
    this.materialImages = {};
    this.materialCanvases = {};
  }
  flushDocumentAssets() {
    // Release all temp canvases that stored intermediate document composites
    releaseCanvases(Object.values(this.tempCanvases));
    // Release all three textures
    Object.values(this.materialTextures).forEach(texture => {
      texture.dispose();
    });
    // Release canvas and texture maps for garbage collection
    this.tempCanvases = {};
    this.materialTextures = {};
    // Clear the material maps for garbage collection
    this.materialMap = {};
    this.hightLightMap = {};
  }
  buildMaterialMap(surfaceMap, meshMap) {
    // Build the substrate and/or finish for every product surface
    Object.keys(surfaceMap).forEach(surfaceKey => {
      const surface = surfaceMap[surfaceKey];
      this.loadAndMapSubstrate(surfaceKey, surface, meshMap);
      if (surface.finishes) {
        surface.finishes.forEach(finish => {
          const finishKey = "".concat(surfaceKey, "-").concat(finish.name);
          this.loadAndMapFinish(finishKey, finish, surface, meshMap);
        });
      }
    });
    // Build materials for all stage elements
    if (this.environment.skyBox) {
      generateTextureFromImage(this.environment.skyBox.textureUrl, this.materialImages, this.materialTextures);
    }
  }
  loadAndMapSubstrate(key, surface, meshMap) {
    var _a;
    if (this.environment) {
      const material = generateSubstrate(surface.page ? "".concat(surface.page, " none") : undefined, surface, this.getTextureSize(surface), this.environment, this.materialImages, this.materialCanvases, this.materialTextures, this.documentCanvases, this.tempCanvases);
      // preserve skin material for SkinnedMesh models with Bones
      if ((meshMap === null || meshMap === void 0 ? void 0 : meshMap[key]) instanceof SkinnedMesh) {
        // TODO: We need to recheck this and use correct types
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        material.skinning = (_a = meshMap[key].material.skinning) !== null && _a !== void 0 ? _a : false;
      }
      this.materialMap[key] = material.clone();
      this.hightLightMap[key] = material.color.clone();
    }
  }
  loadAndMapFinish(key, finish, surface, meshMap) {
    var _a, _b;
    if (this.environment) {
      if (!surface.page) {
        throw new Error("A page number must exist in the surface map to show to show finish: '".concat(finish.channel, "'!"));
      }
      // Get the document and finish keys
      const documentKey = "".concat(surface.page, " none");
      const finishKey = "".concat(surface.page, " ").concat(finish.channel);
      if (!this.documentCanvases[documentKey]) {
        throw new Error("No document information exists for page ".concat(surface.page, "!"));
      }
      if (!this.documentCanvases[finishKey]) {
        throw new Error("No finish information exists on page ".concat(surface.page, " for finish '").concat(finish.channel, "'!"));
      }
      const material = generateFinish(documentKey, finishKey, finish, surface, this.getTextureSize(surface), this.environment, this.materialImages, this.materialCanvases, this.materialTextures, this.documentCanvases, this.tempCanvases);
      // preserve skin material for SkinnedMesh models with Bones
      if ((meshMap === null || meshMap === void 0 ? void 0 : meshMap[key]) instanceof SkinnedMesh) {
        // TODO: We need to recheck this and use correct types
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        material.skinning = (_a = meshMap[key].material.skinning) !== null && _a !== void 0 ? _a : false;
      }
      this.materialMap[key] = material.clone();
      this.hightLightMap[key] = material.color.clone();
      // preserve skin material for SkinnedMesh models with Bones
      if ((meshMap === null || meshMap === void 0 ? void 0 : meshMap[key]) instanceof SkinnedMesh) {
        // TODO: We need to recheck this and use correct types
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        material.skinning = (_b = meshMap[key].material.skinning) !== null && _b !== void 0 ? _b : false;
      }
      this.materialMap[key] = material.clone();
      this.hightLightMap[key] = material.color.clone();
    }
  }
}